import { Button } from "@/components/ui/button";
import { BenefitItem } from "./BenefitItem";
import {
  ChevronRightIcon,
  CreditCardIcon,
  SparklesIcon,
  UsersIcon,
  ZapIcon,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@/hooks/useUserProvider";
import { useMemo, useState } from "react";
import { useGetTiers } from "@/hooks/queries/useGetTiers";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

export const Free = () => {
  const [interval, setInterval] = useState<"monthly" | "yearly">("yearly");

  const { setUserModalOpen, setShowSubscriptionsModal } = useUser();

  const { data: tiers, isLoading, isError, error } = useGetTiers();

  const currentTier = useMemo(
    () => tiers?.find(tier => tier.slug === "plus"),
    [tiers],
  );

  const monthlyPrice = useMemo(
    () => currentTier?.prices.find(price => price.type === "monthly")?.price,
    [currentTier],
  );

  const yearlyPrice = useMemo(
    () => currentTier?.prices.find(price => price.type === "yearly")?.price,
    [currentTier],
  );

  const yearlyDiscount = useMemo(() => {
    if (monthlyPrice == null || yearlyPrice == null || monthlyPrice <= 0) {
      return null;
    }

    return (((monthlyPrice - yearlyPrice / 12) / monthlyPrice) * 100).toFixed(
      2,
    );
  }, [monthlyPrice, yearlyPrice]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-52 rounded-md" />
          <Skeleton className="h-4 w-80 rounded-md" />
        </div>
        <div className="rounded-md bg-gradient-to-br from-primary/5 to-primary/10 p-4">
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-28 rounded-md" />
              <Skeleton className="h-9 w-28 rounded-md" />
              <Skeleton className="h-3 w-32 rounded-md" />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-28 rounded-md" />
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-10 rounded-md" />
            ))}
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-12 rounded-full" />
              <Skeleton className="h-4 w-36 rounded-md" />
            </div>
            <Skeleton className="h-10 w-full rounded-md sm:w-36" />
          </div>
          <Skeleton className="h-3 w-64 rounded-md" />
        </div>
      </div>
    );
  }

  if (isError || !tiers || monthlyPrice == null || yearlyPrice == null) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Subscription prices not found</AlertTitle>
        <AlertDescription>
          {isError && error instanceof Error
            ? error.message
            : "Failed to load subscription prices."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-semibold">No active subscription</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Subscribe to unlock premium features and enhance your experience
        </p>
      </div>
      <div className="rounded-md bg-gradient-to-br from-primary/5 to-primary/10 p-4">
        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground">
              Personal Premium
            </h3>
            <div className="flex items-baseline gap-1">
              <>
                <span className="text-3xl font-bold">{`€${(interval === "monthly" ? monthlyPrice : yearlyPrice / 12).toFixed(2)}`}</span>
                <span className="text-sm text-muted-foreground">/ month</span>
              </>
            </div>
            {interval === "yearly" && (
              <p className="text-xs font-medium text-primary">
                Billed annually: €{yearlyPrice.toFixed(2)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-medium">Premium Benefits</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <BenefitItem
            icon={<SparklesIcon className="h-4 w-4 text-primary" />}
            title="Premium Widgets"
          />
          <BenefitItem
            icon={<ZapIcon className="h-4 w-4 text-primary" />}
            title="Advanced Features"
          />
          <BenefitItem
            icon={<UsersIcon className="h-4 w-4 text-primary" />}
            title="More Integrations"
          />
          <BenefitItem
            icon={<CreditCardIcon className="h-4 w-4 text-primary" />}
            title="Priority Support"
          />
        </div>
      </div>
      <Separator />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Switch
              id="yearly-toggle"
              checked={interval === "yearly"}
              onCheckedChange={value =>
                setInterval(value ? "yearly" : "monthly")
              }
            />
            <label htmlFor="yearly-toggle" className="cursor-pointer text-sm">
              {yearlyDiscount ? `Yearly (${yearlyDiscount}% off)` : "Yearly"}
            </label>
          </div>
          <Button
            className="group w-full transition-all sm:w-auto"
            onClick={() => {
              setUserModalOpen(false);
              setShowSubscriptionsModal(true);
            }}
          >
            Subscribe Now
            <ChevronRightIcon className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Upgrade now and get a 14-day money-back guarantee
        </p>
      </div>
    </div>
  );
};
